import { useContext, useEffect, useRef } from 'react';

import { useClaim } from '~/components/ClaimContainer';
import { ResolvedClaimBarContext } from '~/components/ClaimPage/ResolvedClaimBarContext';
import type { ResolvedClaimFieldsResponseResolvedClaimFieldEntry } from '~/server_shared/openapi/data-contracts';

export type ResolvedClaimBarEntry = ResolvedClaimFieldsResponseResolvedClaimFieldEntry;

interface UseFetchResolvedClaimBarReturnType {
  resolvedClaimBar: ResolvedClaimBarEntry[] | null;
  isWaitingForClaimBar: boolean;
}

/**
 * The claim bar as this claim resolves it, values included, from the fetch ResolvedClaimBarProvider
 * started alongside the claim's. Deliberately not persisted: the payload carries the claim's own
 * values, so it is refetched whenever the claim is.
 */
const useFetchResolvedClaimBar = (): UseFetchResolvedClaimBarReturnType => {
  const { claim, reloadingUuid } = useClaim({ claimRequired: true });
  const resolvedClaimBarFetch = useContext(ResolvedClaimBarContext);
  const reload = resolvedClaimBarFetch?.reload;

  // The uuid outlives a remount, so reload on a change to it, not on its presence. The provider applies
  // only its newest request's answer, so a reload never has to wait for an earlier request.
  const handledReloadingUuid = useRef(reloadingUuid);
  useEffect(() => {
    if (!reload || reloadingUuid === handledReloadingUuid.current) return;
    handledReloadingUuid.current = reloadingUuid;
    reload();
  }, [reload, reloadingUuid]);

  // The provider outlives a move to another claim, so a payload is used only for the claim it is about.
  // Until this claim's payload arrives the bar waits rather than paint the Default it may replace; only
  // a failed request falls back to the Default.
  const data = resolvedClaimBarFetch?.data;
  const resolvedClaimBar = data?.claim_id === claim.id ? data.claim_bar : null;
  const isWaitingForClaimBar = !!resolvedClaimBarFetch && !resolvedClaimBar && !resolvedClaimBarFetch.isError;
  return { resolvedClaimBar, isWaitingForClaimBar };
};

export default useFetchResolvedClaimBar;
